/**
 * The pieces of the shipment page's top bar: the labelled info columns, their
 * tooltips, and the right-hand block with the state and the relevant date.
 *
 * Split from ShipmentTopBar, which keeps the layout and decides which
 * columns a shipment in a given state should show.
 */

import { ToolTipWrapper } from "@/Shared/Components/QuestionBubble";
import { ShipmentState } from "@freightsimple/generated-dashboard-openapi-client/models/ShipmentState";
import { nameForShipmentState } from "@/Helpers/nameForShipmentState";
import { Row } from "@freightsimple/ui-core/ComponentLayout/Row";
import { ShipmentInfoColumn } from "@/Shared/Shipment/CustomComponents/ShipmentInfoColumn";
import type { ReactNode } from "react";

interface InfoColumnProps {
  label: ReactNode;
  value: ReactNode;
}

// One right-aligned label/value column. Both lines stay on one line — a value
// like "Tomorrow, 10am-4:30pm" reads wrong broken in half, and keeping it
// unbreakable is also what stops the whole block being squeezed (see TopRight_).
//
// The rendering lives in ShipmentInfoColumn so the claims card can use the same
// one: it had grown its own smaller, dimmer copy, and the mismatch against this
// bar is what ENG-6743 was raised about.
function InfoColumn_(props: InfoColumnProps) {
  return (
    <ShipmentInfoColumn
      label={props.label}
      value={props.value}
      align="end"
      nowrap
    />
  );
}

export function makeTimeDescription(
  shipmentState: ShipmentState,
  pickupDateDescription: string | undefined,
  expectedDeliveryDateDescription: string | undefined,
  appointmentDateDescription: string | undefined,
  actualDeliveryDateDescription: string | undefined,
): string {
  switch (shipmentState) {
    case ShipmentState.Quoted:
    case ShipmentState.QuoteRequested:
    case ShipmentState.BookingConfirmed:
    case ShipmentState.BookingRequested:
    case ShipmentState.BookingFailed:
      if (pickupDateDescription === undefined) {
        throw new Error("pickupDateDescription missing");
      }
      return pickupDateDescription;
    case ShipmentState.InTransit:
      if (appointmentDateDescription !== undefined) {
        return appointmentDateDescription;
      }

      if (expectedDeliveryDateDescription === undefined) {
        return "Waiting for update";
      } else {
        return expectedDeliveryDateDescription;
      }
    case ShipmentState.OnHold:
      if (pickupDateDescription === undefined) {
        throw new Error("pickupDateDescription missing");
      }
      return `${pickupDateDescription} (On Hold)`;
    case ShipmentState.Delivered:
      if (actualDeliveryDateDescription === undefined) {
        // This happens in the pickup information screen when it's delivered for example
        return "";
      }
      return actualDeliveryDateDescription;
    case ShipmentState.Cancelled:
      return "";
    case ShipmentState.Lost:
      return "";
  }
}

interface TopRightProps {
  shipmentState: ShipmentState;
  referenceNumberLabel: string | undefined;
  referenceNumber: string | undefined;
  billOfLadingNumber: string | undefined;
  timeLabel: string | undefined;
  timeDescription: string;
  timeLabel2: string | undefined;
  timeDescription2: string | undefined;
  timeLabelTooltip: ReactNode | undefined;
}

interface LabelTooltipProps {
  description: ReactNode | undefined;
  children: ReactNode;
}

function LabelTooltip_(props: LabelTooltipProps) {
  if (props.description === undefined) {
    return <>{props.children}</>;
  } else {
    return (
      <ToolTipWrapper description={props.description}>
        {props.children}
      </ToolTipWrapper>
    );
  }
}

export function TopRight_(props: TopRightProps) {
  return (
    // The right-hand info block holds the operational data (status, dates,
    // BOL). It must never be compressed by a busy left side — when squeezed,
    // long values like "Tomorrow, 10am-4:30pm" used to wrap mid-string
    // (ENG-5706). Every value is nowrap, so this block can't shrink below its
    // content and the left block is the one that yields.
    <Row gap={6} align="center">
      {props.billOfLadingNumber && (
        <InfoColumn_ label="BOL#" value={props.billOfLadingNumber} />
      )}

      <InfoColumn_
        label="Status"
        value={nameForShipmentState(props.shipmentState)}
      />

      {props.timeLabel && (
        <LabelTooltip_ description={props.timeLabelTooltip}>
          <InfoColumn_ label={props.timeLabel} value={props.timeDescription} />
        </LabelTooltip_>
      )}

      {props.timeLabel2 && props.timeDescription2 && (
        <LabelTooltip_ description={props.timeLabelTooltip}>
          <InfoColumn_
            label={props.timeLabel2}
            value={props.timeDescription2}
          />
        </LabelTooltip_>
      )}

      {props.referenceNumberLabel && (
        <InfoColumn_
          label={props.referenceNumberLabel}
          value={props.referenceNumber}
        />
      )}
    </Row>
  );
}
